// src/new/components/monitor/TriageChat/TriageSubagentBubble.tsx
import React, { useId, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { MarkdownContent } from 'new/elements/MarkdownContent';
import { SubagentState } from './triageBackend';
// Subagent output (log scout / flow inspector findings) quotes raw XML/JSON
// payloads inline the same way the lead agent's replies do — see
// `markdownOptions.tsx` for why raw-HTML parsing must be disabled and how a
// standalone payload paragraph gets boxed instead of silently vanishing.
import { rawPayloadMarkdownOptions } from './markdownOptions';
import { wrapRawPayloadBlocks } from './payloadBlocks';
import s from './TriageSubagentBubble.module.scss';

export interface TriageSubagentBubbleProps {
  subagent: SubagentState;
}

/** One collapsible inline card per subagent run (Log Scout, Flow Inspector,
 *  etc.), rendered under the Lead agent's bubble. Collapsed by default,
 *  showing just a label + a truncated prompt preview; expands to show the
 *  full task prompt and streamed/streaming output. Used identically by the
 *  live path (`subagent-start`/`text`/`subagent-end` mutating the map) and
 *  the history/reload path (`getHistory` returning the same shape) — there
 *  is no separate "historical" rendering branch. */
export const TriageSubagentBubble: React.FC<TriageSubagentBubbleProps> = ({ subagent }) => {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const label = subagent.title ?? subagent.agent ?? 'Subagent';
  const isStreaming = subagent.status === 'streaming';

  const toggle = () => setExpanded((e) => !e);
  // Belt-and-suspenders keyboard handling: a native <button> already responds
  // to Enter/Space in real browsers, but this makes the toggle explicit and
  // avoids relying on UA default-action behavior jsdom doesn't simulate.
  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className={s.root} data-testid="triage-subagent-bubble">
      <button
        type="button"
        className={s.header}
        onClick={toggle}
        onKeyDown={onKeyDown}
        aria-expanded={expanded}
        aria-controls={detailsId}
        data-testid="triage-subagent-toggle"
      >
        {isStreaming && (
          <CircularProgress size={12} className={s.spinner} data-testid="triage-subagent-spinner" />
        )}
        <span className={s.label}>{label}</span>
        {!expanded && <span className={s.preview}>{subagent.taskPrompt}</span>}
        <span className={s.caret} aria-hidden="true">
          {expanded ? '▴' : '▾'}
        </span>
      </button>
      {expanded && (
        <div className={s.details} id={detailsId}>
          {subagent.taskPrompt && (
            <div className={s.section}>
              <div className={s.sectionLabel}>Task</div>
              <div className={s.taskPrompt}>{subagent.taskPrompt}</div>
            </div>
          )}
          <div className={s.section}>
            <div className={s.sectionLabel}>Output</div>
            <div className={s.output}>
              <MarkdownContent options={rawPayloadMarkdownOptions}>
                {wrapRawPayloadBlocks(subagent.outputText)}
              </MarkdownContent>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
TriageSubagentBubble.displayName = 'TriageSubagentBubble';
